if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');


        this.badgeBehavior = this.querySelector('media-gallery').dataset.badgeBehavior;
        const initialVariant = this.getSelectedVariant(this);
        this.initialVariantMediaId = initialVariant?.featured_media?.id?.toString();

      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        
        const initialVariant = this.getSelectedVariant(this);
        if (initialVariant) {
          // Run filtering immediately on initial load; do not unhide thumbnails first
          setTimeout(() => {
            this.updateImagesByVariantValues(initialVariant, { initial: true });
          }, 50);
        }
        
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            
            if (variant) {
              setTimeout(() => {
                const newProductInfo = document.querySelector(selector);
                if (newProductInfo && newProductInfo.updateImagesByVariantValues) {
                  newProductInfo.updateImagesByVariantValues(variant);
                }
              }, 200);
            }
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            
            if (variant) {
              setTimeout(() => {
                this.updateImagesByVariantValues(variant);
              }, 200);
            }
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);
          setTimeout(() => {
            this.updateImagesByVariantValues(variant);
            setTimeout(() => {
              this.updateMediaGrouping();
            }, 10);
          }, 50);
          this.updateDescription(html);


          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantitySelector(html);  
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          this.updateStickyATC(html);
          this.updateTieredSelectorFromHtml(html);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }


      updateStickyATC(html) {
        const newStickyATCContainer = html.querySelector('.sticky-atc');
        const currentStickyATCContainer = document.querySelector('.sticky-atc');
      
        if (newStickyATCContainer && currentStickyATCContainer) {
          const newContent = newStickyATCContainer.innerHTML.trim();
      
          if (newContent === '') {
            currentStickyATCContainer.classList.add('hidden');
            currentStickyATCContainer.innerHTML = '';
          } else {
            currentStickyATCContainer.classList.remove('hidden');
            currentStickyATCContainer.innerHTML = newContent;
          }
        }
      }
      
      updateDescription(html) {
        const newDescriptionContainer = html.querySelector('.variant__description');
        const currentDescriptionContainer = this.querySelector('.variant__description');
      
        if (newDescriptionContainer && currentDescriptionContainer) {
          const newContent = newDescriptionContainer.innerHTML.trim();
          
          if (newContent === '') {
            currentDescriptionContainer.classList.add('hidden');
            currentDescriptionContainer.innerHTML = '';
          } else {
            currentDescriptionContainer.classList.remove('hidden');
            currentDescriptionContainer.innerHTML = newContent;
          }
        }
      }


      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }


          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );
        
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;

        this.updateBadgeVisibility(variantFeaturedMediaId);

      }


      updateMediaGrouping() {
        const mediaGalleries = this.querySelectorAll('media-gallery');
        mediaGalleries.forEach(mediaGallery => {
          const groupVariants = mediaGallery.getAttribute('data-group-variants');
          const hasOnlyDefaultVariant = mediaGallery.getAttribute('data-has-only-default-variant');
          const disableImageGrouping = mediaGallery.getAttribute('data-disable-image-grouping');
      
          if (groupVariants === 'false' || hasOnlyDefaultVariant === 'true' || disableImageGrouping === 'true') {
            return;
          }
      
          const mediaItems = mediaGallery.querySelectorAll('.product__media-item, .thumbnail-list__item');
          const featuredMedia = Array.from(mediaItems).find(media => media.classList.contains('is-active'));
          const featuredVariantGrouping = featuredMedia ? featuredMedia.getAttribute('data-variant-grouping') : null;
      
          mediaItems.forEach(mediaItem => {
            const isThumbnail = mediaItem.classList.contains('thumbnail-list__item');
            if (isThumbnail) {
              const img = mediaItem.querySelector('button img') || mediaItem.querySelector('img');
              const altText = img ? (img.getAttribute('alt') || '') : '';
              if (this.shouldShowForAlt(altText)) {
                mediaItem.classList.remove('hide-image', 'hidden');
                return;
              }
            }

            if (!isThumbnail) {
              const img = mediaItem.querySelector('img');
              const altText = img ? (img.getAttribute('alt') || '') : '';
              if (this.shouldShowForAlt(altText)) {
                mediaItem.classList.remove('hide-image', 'hidden');
                return;
              }
            }
            
            const mediaVariantGrouping = mediaItem.getAttribute('data-variant-grouping');
            if (mediaVariantGrouping === featuredVariantGrouping) {
              mediaItem.classList.remove('hide-image');
            } else {
              mediaItem.classList.add('hide-image');
            }
          });
      
          const modalContent = this.productModal?.querySelector('.product-media-modal__content');
          if (modalContent) {
            modalContent.querySelectorAll('.product-modal-image').forEach(mediaItem => {
              const img = mediaItem.querySelector('img');
              const altText = img ? (img.getAttribute('alt') || '') : '';
              if (this.shouldShowForAlt(altText)) {
                mediaItem.classList.remove('hide-image', 'hidden');
                return;
              }

              const mediaVariantGrouping = mediaItem.getAttribute('data-variant-grouping');
              mediaItem.classList.toggle('hide-image', mediaVariantGrouping !== featuredVariantGrouping);
            });
          }
        });
      }

      updateImagesByVariantValues(variant, options = {}) {
        if (!variant) return;
        const { initial = false } = options;
        const mediaGalleries = this.querySelectorAll('media-gallery');
        if (!initial) {
          mediaGalleries.forEach(mediaGallery => {
            const thumbnailItems = mediaGallery.querySelectorAll('.thumbnail-list__item');
            thumbnailItems.forEach(thumbnailItem => {
              thumbnailItem.classList.remove('hidden', 'hide-image');
            });
          });
        }

        const selectedVariantValues = [];
        if (variant.option1) selectedVariantValues.push(variant.option1);
        if (variant.option2) selectedVariantValues.push(variant.option2);
        if (variant.option3) selectedVariantValues.push(variant.option3);
        const normalizedVariantValues = selectedVariantValues.map((value) => this.normalizeValue(value));
        const normalizedVariantValueSet = new Set(normalizedVariantValues);
        if (normalizedVariantValues.length === 0) {
          this.querySelectorAll('.product__media-item, .thumbnail-list__item, .product-modal-image').forEach(item => {
            item.classList.remove('hide-image', 'hidden');
          });
          return;
        }

        const imageMatchesVariant = (altText) => {
          if (!altText || altText.trim() === '') {
            return true;
          }

          if (this.altMatchesProductTitle(altText)) {
            return true;
          }
          
          const normalizedAlt = this.normalizeValue(altText);
          const isVariantFormat = altText.includes('/');
          if (normalizedVariantValues.length === 1) {
            const selectedHandle = normalizedVariantValues[0];
            return normalizedAlt.includes(selectedHandle);
          }
          
          const altParts = altText.split('/').map(part => this.normalizeValue(part.trim())).filter(Boolean);
          const altSet = new Set(altParts);
          for (const variantValue of normalizedVariantValues) {
            if (!altSet.has(variantValue)) return false;
          }
          return true;
        };
        if (!document.querySelector('style[data-variant-image-filter]')) {
          const style = document.createElement('style');
          style.setAttribute('data-variant-image-filter', 'true');
          style.textContent = `
            .product__media-item.hide-image:not(.is-active),
            .thumbnail-list__item.hide-image:not(.is-active),
            .product-modal-image.hide-image:not(.is-active) {
              display: none;
            }
          `;
          document.head.appendChild(style);
        }

        if (mediaGalleries.length === 0) {
          const allMediaItems = this.querySelectorAll('.product__media-item, .thumbnail-list__item');
          allMediaItems.forEach(mediaItem => {
            const img = mediaItem.querySelector('button img, img');
            if (img) {
              const altText = img.getAttribute('alt') || '';
              const matches = imageMatchesVariant(altText);
              if (matches) {
                mediaItem.classList.remove('hide-image', 'hidden');
              } else {
                mediaItem.classList.add('hide-image');
                if (!mediaItem.classList.contains('is-active')) {
                  mediaItem.classList.add('hidden');
                } else {
                  mediaItem.classList.remove('hidden');
                }
              }
            } else {
              mediaItem.classList.remove('hide-image', 'hidden');
            }
          });
        } else {
          mediaGalleries.forEach(mediaGallery => {
            const mediaItems = mediaGallery.querySelectorAll('.product__media-item');
            mediaItems.forEach(mediaItem => {
              const img = mediaItem.querySelector('img');
              if (img) {
                const altText = img.getAttribute('alt') || '';
                const matches = imageMatchesVariant(altText);
                if (matches) {
                  mediaItem.classList.remove('hide-image', 'hidden');
                } else {
                  mediaItem.classList.add('hide-image');
                  if (!mediaItem.classList.contains('is-active')) {
                    mediaItem.classList.add('hidden');
                  } else {
                    mediaItem.classList.remove('hidden');
                  }
                }
              }
            });

            const thumbnailItems = mediaGallery.querySelectorAll('.thumbnail-list__item');
            thumbnailItems.forEach(thumbnailItem => {
              thumbnailItem.classList.remove('hide-image', 'hidden');
              const img = thumbnailItem.querySelector('button img, img');
              if (img) {
                const altText = img.getAttribute('alt') || '';
                const matches = imageMatchesVariant(altText);
                if (!matches && altText && altText.trim() !== '') {
                  thumbnailItem.classList.add('hide-image');
                  const isActive = thumbnailItem.classList.contains('is-active') || 
                                  thumbnailItem.querySelector('button[aria-current="true"]');
                  if (!isActive) {
                    thumbnailItem.classList.add('hidden');
                  }
                }
              }
            });
          });
        }
        const modalContent = this.productModal?.querySelector('.product-media-modal__content');
        if (modalContent) {
          modalContent.querySelectorAll('.product-modal-image').forEach(mediaItem => {
            const img = mediaItem.querySelector('img');
            if (img) {
              const altText = img.getAttribute('alt') || '';
              const matches = imageMatchesVariant(altText);
              if (matches) {
                mediaItem.classList.remove('hide-image', 'hidden');
              } else {
                mediaItem.classList.add('hide-image');
                if (!mediaItem.classList.contains('is-active')) {
                  mediaItem.classList.add('hidden');
                } else {
                  mediaItem.classList.remove('hidden');
                }
              }
            }
          });
        } 

        this.reinitializeSliders();
        setTimeout(() => {
          try {
            const galleries = this.querySelectorAll('media-gallery');
            galleries.forEach(mediaGallery => {
              const firstVisibleThumb = Array.from(mediaGallery.querySelectorAll('.thumbnail-list__item'))
                .find(item => !item.classList.contains('hidden') &&
                              !item.classList.contains('hide-image') &&
                              item.offsetParent !== null);

              if (firstVisibleThumb) {
                const button = firstVisibleThumb.querySelector('button');
                if (button) {
                  const clickEvt = new MouseEvent('click', { bubbles: true, cancelable: true, view: window });
                  button.dispatchEvent(clickEvt);
                }
              }
              // Show gallery after items are finalized
              mediaGallery.style.opacity = '1';
            });
          } catch (e) {
            console.warn('Auto-selecting first visible thumbnail failed:', e);
          }
        }, 120);
      }

      reinitializeSliders() {
        const mediaGalleries = this.querySelectorAll('media-gallery');
        mediaGalleries.forEach(mediaGallery => {
          const sliderComponents = mediaGallery.querySelectorAll('slider-component');
          sliderComponents.forEach(sliderComponent => {
            if (!sliderComponent.slider) {
            }

            try {
              const allItems = sliderComponent.querySelectorAll('[id^="Slide-"]');
              const visibleItems = Array.from(allItems).filter(item => {
                return !item.classList.contains('hidden') && 
                       !item.classList.contains('hide-image') && 
                       item.offsetParent !== null;
              });
             
              if (visibleItems.length === 0) {
                return;
              }

              sliderComponent.sliderItems = visibleItems;

              if (sliderComponent.resetPages) {
                if (sliderComponent.initPages) {
                  sliderComponent.initPages();
                } else {
                  sliderComponent.resetPages();
                }
              } else if (sliderComponent.initPages) {
                sliderComponent.initPages();
              }
              
              if (sliderComponent.update) {
                setTimeout(() => {
                  try {
                    if (sliderComponent.sliderItemsToShow && 
                        sliderComponent.sliderItemsToShow.length > 0 &&
                        sliderComponent.sliderItemOffset !== undefined) {
                      sliderComponent.update();
                    }
                  } catch (error) {
                    console.warn('Slider update error:', error);
                  }
                }, 50);
              }
            } catch (error) {
              console.warn('Slider re-initialization error:', error);
            }
          });

          const sliderCounterTotal = mediaGallery.querySelector('.slider-counter--total');
          if (sliderCounterTotal) {
            try {
              const visibleItems = Array.from(mediaGallery.querySelectorAll('[id^="Slide-"]')).filter(item => {
                return !item.classList.contains('hidden') && 
                       !item.classList.contains('hide-image') && 
                       item.offsetParent !== null;
              });
              sliderCounterTotal.textContent = visibleItems.length || 1;
            } catch (error) {
              console.warn('Slider counter update error:', error);
            }
          }
        });
      }
      
      updateBadgeVisibility(variantFeaturedMediaId) {
        const mediaItems = this.querySelectorAll('.product__media-item');
        mediaItems.forEach(item => {
          const badgeContainer = item.querySelector('.product__badge-container');
          if (badgeContainer) {
            const isActiveMedia = item.dataset.mediaId === `${this.dataset.section}-${variantFeaturedMediaId}`;
      
            if (this.badgeBehavior === 'featured') {
              badgeContainer.classList.toggle('badge--hidden', !isActiveMedia);
            } else if (this.badgeBehavior === 'initial') {
              const mediaItemId = item.dataset.mediaId.split('-').pop();
              const isInitialVariantMedia = mediaItemId === this.initialVariantMediaId;
              badgeContainer.classList.toggle('badge--hidden', !(isActiveMedia && isInitialVariantMedia));
            }
          }
        });
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      updateQuantitySelector(html) {
        const newQuantitySelectorContainer = html.querySelector('#Quantity-Form-' + this.sectionId);
        const currentQuantitySelectorContainer = document.querySelector('#Quantity-Form-' + this.dataset.section);
        
        if (newQuantitySelectorContainer && currentQuantitySelectorContainer) {
          const newContent = newQuantitySelectorContainer.innerHTML.trim();
          
          if (newContent === '') {
            return;
          }
          
          currentQuantitySelectorContainer.innerHTML = newContent;
          
          this.quantityInput = currentQuantitySelectorContainer.querySelector('.quantity__input');
          this.initQuantityHandlers();
        }
      }
      
      updateTieredSelectorFromHtml(html) {
        const updatedOptions = html.querySelector(
          `.tiered-quantity-options[data-quantity-input-id="Quantity-${this.sectionId}"]`
        );
        if (!updatedOptions) return;
      
        const updatedContainer = updatedOptions.closest('.tiered-quantity-selector');
        if (!updatedContainer) return;
      
        const existingOptions = this.querySelector(
          `.tiered-quantity-options[data-quantity-input-id="Quantity-${this.sectionId}"]`
        );
        if (!existingOptions) return;
      
        const existingContainer = existingOptions.closest('.tiered-quantity-selector');
        if (!existingContainer) return;
      
        existingContainer.innerHTML = updatedContainer.innerHTML;
        this.initTieredSelectorEvents();
      }
      
      initTieredSelectorEvents() {
        const container = this.querySelector(
          `.tiered-quantity-options[data-quantity-input-id="Quantity-${this.sectionId}"]`
        );
        if (!container) return;
      
        const quantityInput = document.getElementById(container.dataset.quantityInputId);
        const buttons = container.querySelectorAll('.tiered-option');
      
        buttons.forEach((btn) => {
          btn.addEventListener('click', function() {
            buttons.forEach((b) => {
              b.classList.remove('tiered-option--active');
              b.style.background = '#fff';
              b.style.color = 'var(--text-color-unselected)';
            });
            btn.classList.add('tiered-option--active');
            btn.style.background = getComputedStyle(btn).getPropertyValue('--selected-bg-color') || '#000';
            btn.style.color = 'var(--text-color-selected)';
      
            const qty = parseInt(btn.getAttribute('data-quantity'), 10);
            if (quantityInput && !isNaN(qty)) {
              quantityInput.value = qty;
              quantityInput.dispatchEvent(new Event('change'));
            }
          });
        });
      }
      
      shouldShowForAlt(altText) {
        if (!altText || altText.trim() === '') return true;
        return this.altMatchesProductTitle(altText);
      }

      altMatchesProductTitle(altText = '') {
        if (!altText) return false;
        const normalizedTitle = this.normalizedProductTitle;
        if (!normalizedTitle) return false;
        return this.normalizeValue(altText) === normalizedTitle;
      }

      normalizeValue(value) {
        if (value === undefined || value === null) return '';
        return value.toString().toLowerCase().trim().replace(/\s+/g, '_').replace(/-/g, '_');
      }

      get normalizedProductTitle() {
        if (this._normalizedProductTitle === undefined) {
          const title = this.productTitle;
          this._normalizedProductTitle = title ? this.normalizeValue(title) : '';
        }
        return this._normalizedProductTitle;
      }

      get productTitle() {
        return this.dataset.productTitle || '';
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
